import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ViewType } from '../types/aemDataModels';
import { FilterData, FilterSortBy } from '../utils/search/searchModel';
import { CourseCardProps } from '../../components/app/base/CourseCard/CourseCard.types';
import { CommonFilterViewProps } from '../../components/app/base/FilteredCardsView/FilteredCardsView.types';
import { TabConfig } from '../../components/app/compound/Learning/Learning.types';
import { Api } from '../types/apiSpec';

export type CardItemProps = CourseCardProps;
export type GeneralCardItemProps = CardItemProps | Api.MicrolearningLibrary;
export type FiltersInitialViewContextType = {
  from: number;
  to: number;
  isInEditor: boolean;
  updateFilters: (data: FilterData<any>, page?: number) => void;
  onClear: () => void;
  filters: FilterData<any>;
  courses?: CardItemProps[];
  achievements?: Api.AchievementBase[];
  withSearch?: boolean;
  sortByOptions: FilterSortBy[];
  isAchievements?: boolean;
  fetchingError: null | string;
  tabConfig?: TabConfig[];
  activeTab?: TabConfig;
  handleChangeTab?: (id: string) => void;
  hideTabs?: boolean;
} & Omit<CommonFilterViewProps, 'facets'>;

export type FilterViewContextType = {
  totalPages: number;
  viewType: ViewType;
  setViewType: (view: ViewType) => void;
  sortByOptions: FilterSortBy[];
} & FiltersInitialViewContextType;

const FiltersViewContext = createContext<FilterViewContextType>({} as FilterViewContextType);

export default ({ children, value }: { children: ReactNode; value: FiltersInitialViewContextType }) => {
  const { filters, defaultViewType, pageSize, sortByOptions, fetchingError = null } = value;
  const { totalCount = 0 } = filters;
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const [viewType, setViewType] = useState(defaultViewType);

  const transformedValue: FilterViewContextType = useMemo(
    () => ({
      ...value,
      totalPages,
      viewType,
      setViewType,
      sortByOptions,
      fetchingError,
    }),
    [totalPages, value, viewType],
  );

  useEffect(() => {
    setViewType(defaultViewType);
  }, [defaultViewType]);

  return <FiltersViewContext.Provider value={transformedValue}>{children}</FiltersViewContext.Provider>;
};

export const useFiltersView = () => useContext(FiltersViewContext);
